import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiInstance from '@/utils/api'
import { useI18n } from 'vue-i18n'

export interface Country {
  title: string | Record<string, string>
  value: number
  c_code?: string
}

export const useCountriesStore = defineStore('countries', () => {
  // Safely get i18n instance, fallback to dummy functions if not available
  let t: (key: string) => string
  let locale: { value: string }
  
  try {
    const i18n = useI18n()
    t = i18n.t
    locale = i18n.locale
  } catch {
    // Fallback for when Vue context is not available (during reset)
    t = (key: string) => key
    locale = { value: 'en' }
  }
  
  const rawCountries = ref<Country[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isLoaded = ref(false)

  // Helper function to get localized title
  const getLocalizedTitle = (title: string | Record<string, string>): string => {
    if (typeof title === 'string') {
      try {
        const parsed = JSON.parse(title)
        return parsed[locale.value] || parsed.en || title
      } catch {
        return title
      }
    }
    return title[locale.value] || title.en || String(title)
  }

  // Computed property for localized countries that reacts to locale changes
  const countries = computed(() => 
    rawCountries.value.map(country => ({
      ...country,
      title: getLocalizedTitle(country.title)
    }))
  )

  const fetchCountries = async () => {
    // Skip if already loaded
    if (isLoaded.value) {
      return
    }

    loading.value = true
    error.value = null

    try {
      const response = await apiInstance.get('/get-countries')

      if (response.data.error === false && response.data.data) {
        // Handle both array and object response structures
        const countryData = Array.isArray(response.data.data) ? response.data.data : response.data.data.countries || []

        if (Array.isArray(countryData)) {
          rawCountries.value = countryData
          isLoaded.value = true
        } else {
          throw new Error(t('restaurants.errors.countriesDataNotArray'))
        }
      } else {
        error.value = response.data.message || t('restaurants.errors.failedToFetchCountries')
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('restaurants.errors.networkErrorFetchingCountries')
      console.error('[Countries Store] Error:', {
        error: error.value,
        endpoint: '/get-countries',
        exception: e instanceof Error ? e.message : String(e),
      })
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    rawCountries.value = []
    loading.value = false
    error.value = null
    isLoaded.value = false
  }

  return {
    countries,
    loading,
    error,
    isLoaded,
    fetchCountries,
    getLocalizedTitle,
    reset
  }
})
